import React from "react";
import { Button, Card, CardBody, Divider, Input, Radio, RadioGroup, Textarea, Checkbox, Link, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@heroui/react";
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { BASE_URL } from "../services/config";
import CdekCityAutocomplete from "../components/CdekCityAutocomplete";
import AddressAutocomplete from "../components/AddressAutocomplete";
// Note: Map removed - using simple dropdown list for PVZ
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { useCart } from "../contexts/cart-context"; // Fixed import path to the correct location
import { useAuth } from "../contexts/auth-context";
import api from "../services/api";
import { fetchProductDetail } from "../services/catalog-service";

interface CartItem {
  id: number;
  name: string;
  category: string;
  price: number;
  quantity: number;
  image: string;
}

export const CheckoutPage: React.FC = () => {
  const location = useLocation();
  // Use the cart context to get the actual items and cart functions
  const { items, totalItems, totalPrice, updateQuantity, removeItem } = useCart();
  
  // deliveryMethod: undefined until user explicitly chooses a delivery option
  const [deliveryMethod, setDeliveryMethod] = React.useState<string | undefined>(undefined);
  const [paymentMethod, setPaymentMethod] = React.useState("card_online");
  const [agreeTerms, setAgreeTerms] = React.useState(false);
  const [createAccount, setCreateAccount] = React.useState(false);
  const [orderSubmitted, setOrderSubmitted] = React.useState(false);
  const [promoCode, setPromoCode] = React.useState("");
  const [promoApplied, setPromoApplied] = React.useState(false);
  const [promoMessage, setPromoMessage] = React.useState<string | null>(null);
  const [promoData, setPromoData] = React.useState<null | {
    valid: boolean;
    discount_type?: 'percent' | 'fixed';
    amount?: string; // percent or fixed amount
    discount_amount?: string; // calculated discount from backend (optional)
    message?: string;
  }>(null);
  // Address fields (populated by AddressAutocomplete)
  const [city, setCity] = React.useState("");
  const [street, setStreet] = React.useState("");
  const [house, setHouse] = React.useState("");
  const [flat, setFlat] = React.useState("");
  const [postalCode, setPostalCode] = React.useState("");
  // FIAS/KLADR and manual flags
  const [cityFias, setCityFias] = React.useState<string | null>(null);
  const [cityKladr, setCityKladr] = React.useState<string | null>(null);
  const [cityCdekUuid, setCityCdekUuid] = React.useState<string | null>(null);
  const [cityCdekCode, setCityCdekCode] = React.useState<number | null>(null);
  // CDEK tariffs
  const [cdekTariffs, setCdekTariffs] = React.useState<any[] | null>(null);
  const [cdekLoading, setCdekLoading] = React.useState(false);
  const [cdekError, setCdekError] = React.useState<string | null>(null);
  const [selectedTariffCode, setSelectedTariffCode] = React.useState<number | null>(null);
  // PVZ (pickup points) state
  const [deliveryPoints, setDeliveryPoints] = React.useState<any[] | null>(null);
  const [deliveryPointsLoading, setDeliveryPointsLoading] = React.useState(false);
  const [deliveryPointsError, setDeliveryPointsError] = React.useState<string | null>(null);
  // full cached list of PVZ fetched once per tariff/city selection
  const [deliveryPointsAll, setDeliveryPointsAll] = React.useState<any[] | null>(null);
  const [selectedPVZUuid, setSelectedPVZUuid] = React.useState<string | null>(null);
  const [pvzFilterText, setPvzFilterText] = React.useState<string>('');
  const [pvzFocused, setPvzFocused] = React.useState<boolean>(false);
  // FIAS ids for city/street/house when 'до двери' selected
  const [selected_city_fias, setSelectedCityFias] = React.useState<string | null>(null);
  const [selected_street_fias, setSelectedStreetFias] = React.useState<string | null>(null);
  const [selected_house_fias, setSelectedHouseFias] = React.useState<string | null>(null);
  // address input pieces for 'до двери'
  const [streetInput, setStreetInput] = React.useState<string>('');
  const [houseInput, setHouseInput] = React.useState<string>('');
  const [flatInput, setFlatInput] = React.useState<string>('');
  const [floorInput, setFloorInput] = React.useState<string>('');
  const [intercomCodeInput, setIntercomCodeInput] = React.useState<string>('');
  const [addressCommentInput, setAddressCommentInput] = React.useState<string>('');
  const [addrLoading, setAddrLoading] = React.useState(false);
  const [addrError, setAddrError] = React.useState<string | null>(null);
  const [streetSuggestions, setStreetSuggestions] = React.useState<any[]>([]);
  const [houseSuggestions, setHouseSuggestions] = React.useState<any[]>([]);
  const [showStreetSuggestions, setShowStreetSuggestions] = React.useState(false);
  const [showHouseSuggestions, setShowHouseSuggestions] = React.useState(false);
  const [streetFias, setStreetFias] = React.useState<string | null>(null);
  const [streetKladr, setStreetKladr] = React.useState<string | null>(null);
  const [houseFias, setHouseFias] = React.useState<string | null>(null);
  const [houseKladr, setHouseKladr] = React.useState<string | null>(null);
  const [addressError, setAddressError] = React.useState<string | null>(null);
  // customer fields
  const [customerName, setCustomerName] = React.useState("");
  const [customerSurname, setCustomerSurname] = React.useState("");
  const [customerEmail, setCustomerEmail] = React.useState("");
  const [customerPhone, setCustomerPhone] = React.useState("");
  const [comment, setComment] = React.useState("");
  const [orderError, setOrderError] = React.useState<string | null>(null);
  const [orderId, setOrderId] = React.useState<string | null>(null);
  // Client-only fallback order id (generated after submit) to avoid server/client mismatch
  const [clientOrderFallback, setClientOrderFallback] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, boolean>>({});
  const contactInfoRef = React.useRef<HTMLElement | null>(null);
  const citySectionRef = React.useRef<HTMLElement | null>(null);
  const deliverySectionRef = React.useRef<HTMLElement | null>(null);
  const pvzSectionRef = React.useRef<HTMLElement | null>(null);
  const addressSectionRef = React.useRef<HTMLElement | null>(null);
  const termsRef = React.useRef<HTMLElement | null>(null);

  const clearFieldError = React.useCallback((key: string) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // Generate a client-only fallback order id when order is submitted but backend didn't return one.
  // This runs only on the client (useEffect) to avoid introducing randomness into server render.
  React.useEffect(() => {
    if (orderSubmitted && !orderId && !clientOrderFallback) {
      const fallback = `ORD-${Math.floor(100000 + Math.random() * 900000)}`;
      setClientOrderFallback(fallback);
    }
  }, [orderSubmitted, orderId, clientOrderFallback]);
  // Stock issues modal state
  const [stockModalOpen, setStockModalOpen] = React.useState(false);
  const [stockIssues, setStockIssues] = React.useState<any[] | null>(null);
  const [stockActionLoading, setStockActionLoading] = React.useState(false);

  // Update delivery cost calculation based on method
  const calculateDelivery = () => {
    // Courier for Moscow (1-2 days) and СДЭК for Russia
    if (deliveryMethod === "courier") return 400;
    if (deliveryMethod === "cdek") {
      // if a tariff is selected, use its delivery_sum
      if (selectedTariffCode && Array.isArray(filteredCdekTariffs)) {
        const t = filteredCdekTariffs.find((tt: any) => Number(tt.tariff_code) === Number(selectedTariffCode));
        if (t && typeof t.delivery_sum === 'number') return Math.round(t.delivery_sum);
      }
      // fallback default
      return 350;
    }
    return 0;
  };

  // When city (CDEK code) or cart items change, fetch available tariffs
  React.useEffect(() => {
    const code = cityCdekCode;
    if (!code || items.length === 0) {
      setCdekTariffs(null);
      setSelectedTariffCode(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setCdekLoading(true);
      setCdekError(null);
      try {
        const body = {
          items: items.map(i => ({ product_id: i.id, quantity: i.quantity })),
          to_location_code: code,
          from_location_code: 44,
          type: 1,
        };
        const resp = await api.post('/shipping/cdek/tarifflist/', body, { headers: { 'Content-Type': 'application/json' } });
        const data = resp?.data;
        if (cancelled) return;
        // Support multiple response shapes returned by different backend implementations
        // Common shapes:
        // - { body: { tariff_codes: [...] } }
        // - { tariffs: [...] }
        // - { body: { tariffs: [...] } }
        // - directly an array
        let tariffsList: any[] = [];
        if (!data) {
          tariffsList = [];
        } else if (Array.isArray(data.body?.tariff_codes)) {
          tariffsList = data.body.tariff_codes;
        } else if (Array.isArray(data.body?.tariffs)) {
          tariffsList = data.body.tariffs;
        } else if (Array.isArray(data.tariffs)) {
          tariffsList = data.tariffs;
        } else if (Array.isArray(data)) {
          tariffsList = data;
        } else {
          tariffsList = [];
        }
        setCdekTariffs(tariffsList);
      } catch (err: any) {
        console.error('CDEK tariffs fetch error', err);
        setCdekError(err?.response?.data?.message || 'Ошибка при получении тарифов');
        setCdekTariffs([]);
      } finally {
        if (!cancelled) setCdekLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [cityCdekCode, items]);

  // Temporarily DO NOT filter tariffs — show all tariffs returned by СДЭК.
  // Previously we filtered specific tariff codes; per request we keep all and let user choose.
  const filteredCdekTariffs = React.useMemo(() => {
    if (!Array.isArray(cdekTariffs)) return [];
    // Ensure tariff_code is numeric for downstream comparisons
    return cdekTariffs.map((t: any) => ({ ...t, tariff_code: Number(t.tariff_code) }));
  }, [cdekTariffs]);

  // Determine the currently selected tariff object and its type based on the name text.
  const selectedTariff = React.useMemo(() => {
    if (!Array.isArray(filteredCdekTariffs) || !selectedTariffCode) return null;
    return filteredCdekTariffs.find((t: any) => Number(t.tariff_code) === Number(selectedTariffCode)) || null;
  }, [filteredCdekTariffs, selectedTariffCode]);

  // Determine tariff type. If both 'склад' and 'двер' present in name prefer 'двер' (do not treat as PVZ)
  const selectedTariffIsDoor = Boolean(selectedTariff && String(selectedTariff.tariff_name || '').toLowerCase().includes('двер'));
  const selectedTariffIsPvz = Boolean(selectedTariff && (() => {
    const name = String(selectedTariff.tariff_name || '').toLowerCase();
    return name.includes('склад') && !name.includes('двер');
  })());
  const hasContactFieldErrors = Boolean(fieldErrors.customerName || fieldErrors.customerSurname || fieldErrors.customerEmail || fieldErrors.customerPhone);
  const hasDeliveryErrors = Boolean(fieldErrors.delivery || fieldErrors.cdekTariff);
  const hasAddressErrors = Boolean(fieldErrors.streetInput || fieldErrors.houseInput || fieldErrors.flatInput);
  const errorInputWrapper = 'border border-danger !ring-danger focus-visible:ring-danger focus-visible:ring-2 transition-colors';

  // Temporarily disable auto-selecting the cheapest tariff — require explicit user selection
  /*
  React.useEffect(() => {
    if (!Array.isArray(filteredCdekTariffs) || filteredCdekTariffs.length === 0) return;
    if (selectedTariffCode) return; // already selected
    const sorted = [...filteredCdekTariffs].sort((a,b) => (a.delivery_sum||0) - (b.delivery_sum||0));
    setSelectedTariffCode(sorted[0].tariff_code);
    setDeliveryMethod('cdek');
  }, [filteredCdekTariffs]);
  */

  // When selected tariff is PVZ, fetch delivery points for the chosen city via backend
  React.useEffect(() => {
    let cancelled = false;
    const doFetch = async () => {
      // show PVZ only when selected tariff name indicates 'склад'
      if (!selectedTariffIsPvz) {
        setDeliveryPointsAll(null);
        setSelectedPVZUuid(null);
        return;
      }
      if (!cityCdekCode) return;
      // Fetch full PVZ list once for the selected city/tariff and cache it locally
      setDeliveryPointsAll(null);
      setDeliveryPointsLoading(true);
      setDeliveryPointsError(null);
      try {
        const body: any = { city_code: cityCdekCode };
        const resp = await api.post('/shipping/cdek/deliverypoints/', body, { headers: { 'Content-Type': 'application/json' } });
        const data = resp?.data;
        let list: any[] = [];
        if (!data) list = [];
        else if (Array.isArray(data.body)) list = data.body;
        else if (Array.isArray(data)) list = data;
        else if (Array.isArray(data.body?.delivery_points)) list = data.body.delivery_points;
        else if (Array.isArray(data.body?.pvz)) list = data.body.pvz;
        else if (Array.isArray(data.delivery_points)) list = data.delivery_points;
        else list = [];
        if (!cancelled) setDeliveryPointsAll(list);
      } catch (err: any) {
        console.error('PVZ initial load error', err);
        if (!cancelled) setDeliveryPointsError(err?.response?.data?.message || 'Ошибка при загрузке ПВЗ');
      } finally {
        if (!cancelled) setDeliveryPointsLoading(false);
      }
    };
    doFetch();
    return () => { cancelled = true; };
  }, [selectedTariffIsPvz, cityCdekCode]);

  // Debounced PVZ search by text (q). When user types in pvzFilterText, query backend for matching PVZs.
  // Client-side filtering of cached PVZ list — no network calls here
  const visibleDeliveryPoints = React.useMemo(() => {
    if (!Array.isArray(deliveryPointsAll)) return [];
    const q = (pvzFilterText || '').trim().toLowerCase();
    if (!q) return deliveryPointsAll;
    return deliveryPointsAll.filter((p: any) => {
      const name = String(p.name || p.code || '').toLowerCase();
      const addr = String(p.location?.address || p.address_full || p.address || '').toLowerCase();
      const code = String(p.code || p.uuid || '').toLowerCase();
      return name.includes(q) || addr.includes(q) || code.includes(q);
    });
  }, [deliveryPointsAll, pvzFilterText]);

  // delivery points to display when dropdown is open (limit when empty query)
  const displayDeliveryPoints = React.useMemo(() => {
    if (!pvzFocused) return [];
    if (!Array.isArray(deliveryPointsAll)) return [];
    const q = (pvzFilterText || '').trim();
    if (!q) return deliveryPointsAll.slice(0, 50);
    return visibleDeliveryPoints;
  }, [pvzFocused, deliveryPointsAll, pvzFilterText, visibleDeliveryPoints]);



  // Debounced street suggest -> save street fias
  React.useEffect(() => {
    if (!cityKladr) return;
    const v = streetInput?.trim();
    if (!v) return;
    // If we recently selected a suggestion and want to skip fetching until user edits, bail out
    // (refs defined below)
    // NOTE: we check refs by closure - they will be added below.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (typeof skipStreetFetchRef !== 'undefined' && skipStreetFetchRef.current && typeof lastSelectedStreetRef !== 'undefined' && lastSelectedStreetRef.current === v) {
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        // call backend street suggest with kladr (per contract)
        let resp = await api.get('/address/suggest/street/', { params: { q: v, kladr: cityKladr, count: 10 } });
        let data = resp?.data;
        let suggestions = data?.suggestions || data || [];
        // fallback: try without kladr if no results
        if ((!Array.isArray(suggestions) || suggestions.length === 0)) {
          resp = await api.get('/address/suggest/street/', { params: { q: v, count: 10 } });
          data = resp?.data;
          suggestions = data?.suggestions || data || [];
        }
        const first = Array.isArray(suggestions) && suggestions.length > 0 ? suggestions[0] : null;
        const fias = first?._raw?.data?.fias || first?._raw?.data?.fias_id || first?.raw?.data?.fias || first?.raw?.data?.fias_id || first?.data?.fias || first?.data?.fias_id || first?.fias || null;
        console.debug('Street suggest', { q: v, suggestionsCount: Array.isArray(suggestions) ? suggestions.length : 0, foundFias: fias });
        if (!cancelled) {
          setSelectedStreetFias(fias || null);
          setStreetSuggestions(Array.isArray(suggestions) ? suggestions : []);
          setShowStreetSuggestions(Array.isArray(suggestions) && suggestions.length > 0);
        }
      } catch (err: any) {
        console.error('Street suggest error', err);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [streetInput, cityKladr]);

  // Debounced house suggest -> save house fias
  React.useEffect(() => {
    if (!cityKladr) return;
    const v = houseInput?.trim();
    if (!v) return;
    // If we recently selected a suggestion and want to skip fetching until user edits, bail out
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (typeof skipHouseFetchRef !== 'undefined' && skipHouseFetchRef.current && typeof lastSelectedHouseRef !== 'undefined' && lastSelectedHouseRef.current === v) {
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        // call backend house suggest with street_fias when available
        const params: any = { q: v, count: 10 };
        if (selected_street_fias) params.street_fias = selected_street_fias;
        let resp = await api.get('/address/suggest/house/', { params });
        let data = resp?.data;
        let suggestions = data?.suggestions || data || [];
        if ((!Array.isArray(suggestions) || suggestions.length === 0)) {
          // fallback: try without street_fias
          resp = await api.get('/address/suggest/house/', { params: { q: v, count: 10 } });
          data = resp?.data;
          suggestions = data?.suggestions || data || [];
        }
        const first = Array.isArray(suggestions) && suggestions.length > 0 ? suggestions[0] : null;
        const fias = first?._raw?.data?.fias || first?._raw?.data?.fias_id || first?.raw?.data?.fias || first?.raw?.data?.fias_id || first?.data?.fias || first?.data?.fias_id || first?.fias || null;
        console.debug('House suggest', { q: v, suggestionsCount: Array.isArray(suggestions) ? suggestions.length : 0, foundFias: fias });
        if (!cancelled) {
          setSelectedHouseFias(fias || null);
          setHouseSuggestions(Array.isArray(suggestions) ? suggestions : []);
          setShowHouseSuggestions(Array.isArray(suggestions) && suggestions.length > 0);
        }
      } catch (err: any) {
        console.error('House suggest error', err);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [houseInput, cityKladr, selected_street_fias]);

  // Refs to prevent re-fetching after programmatic selection until user edits
  const lastSelectedStreetRef = React.useRef<string | null>(null);
  const skipStreetFetchRef = React.useRef<boolean>(false);
  const lastSelectedHouseRef = React.useRef<string | null>(null);
  const skipHouseFetchRef = React.useRef<boolean>(false);

  // Input change handlers that clear the 'skip' state when user manually edits
  const handleStreetInputChange = (v: string) => {
    if (lastSelectedStreetRef.current && v !== lastSelectedStreetRef.current) {
      lastSelectedStreetRef.current = null;
      skipStreetFetchRef.current = false;
    }
    setStreetInput(v);
    if (v && v.trim()) clearFieldError('streetInput');
  };

  const handleHouseInputChange = (v: string) => {
    if (lastSelectedHouseRef.current && v !== lastSelectedHouseRef.current) {
      lastSelectedHouseRef.current = null;
      skipHouseFetchRef.current = false;
    }
    setHouseInput(v);
    if (v && v.trim()) clearFieldError('houseInput');
  };
  
  // Calculate the final total with delivery
  // Helpers for suggestions
  const getSuggestionDisplay = (s: any) => {
    // If this suggestion looks like a house suggestion (has house/number fields) prefer house display
    const houseType = s?.house_type || s?.data?.house_type || s?._raw?.data?.house_type || s?.type || '';
    const houseNum = s?.house || s?.data?.house || s?._raw?.data?.house || s?.number || '';
    const blockType = s?.block_type || s?.data?.block_type || s?._raw?.data?.block_type || s?.building_type || '';
    const block = s?.block || s?.data?.block || s?._raw?.data?.block || s?.building || '';
    const houseParts: string[] = [];
    if (houseType) houseParts.push(houseType);
    if (houseNum) houseParts.push(houseNum);
    if (blockType) houseParts.push(blockType);
    if (block) houseParts.push(block);
    const houseDisplay = houseParts.join(' ').trim();
    if (houseDisplay) return houseDisplay;

    // Otherwise prefer explicit street_with_type for street suggestions
    const streetWithType = s?.street_with_type || s?.data?.street_with_type || s?._raw?.data?.street_with_type || null;
    if (streetWithType) return streetWithType;

    return s?.value || s?.display_name || s?.raw?.full_name || s?._raw?.full_name || s?.unrestricted_value || s?.data?.name || s?.name || '';
  };

  const extractFiasFromSuggestion = (s: any) => {
    return s?._raw?.data?.fias || s?._raw?.data?.fias_id || s?.raw?.data?.fias || s?.raw?.data?.fias_id || s?.data?.fias || s?.data?.fias_id || s?.fias || s?.kladr || null;
  };

  const handleSelectStreetSuggestion = (s: any) => {
    const text = getSuggestionDisplay(s);
    const fias = extractFiasFromSuggestion(s);
    // remember and mark to skip subsequent fetches triggered by programmatic set
    lastSelectedStreetRef.current = text || null;
    skipStreetFetchRef.current = true;
    setStreetInput(text || '');
    setSelectedStreetFias(fias || null);
    setStreetSuggestions([]);
    setShowStreetSuggestions(false);
    if (text) clearFieldError('streetInput');
  };

  const handleSelectHouseSuggestion = (s: any) => {
    const text = getSuggestionDisplay(s);
    const fias = extractFiasFromSuggestion(s);
    // remember and mark to skip subsequent fetches triggered by programmatic set
    lastSelectedHouseRef.current = text || null;
    skipHouseFetchRef.current = true;
    setHouseInput(text || '');
    setSelectedHouseFias(fias || null);
    setHouseSuggestions([]);
    setShowHouseSuggestions(false);
    if (text) clearFieldError('houseInput');
  };

  const handleSelectPVZ = (p: any) => {
    setSelectedPVZUuid(p?.uuid || p?.code || null);
    clearFieldError('pvz');
  };
  const calculatePromoDiscount = (): number => {
    if (!promoApplied || !promoData || !promoData.valid) return 0;
    // Discount must apply only to items subtotal, not to delivery
    const base = totalPrice;
    try {
      if (promoData.discount_type === 'percent') {
        const pct = parseFloat(promoData.amount || '0');
        if (isNaN(pct) || pct <= 0) return 0;
        const disc = Math.round((base * pct) / 100);
        return disc;
      }
      if (promoData.discount_type === 'fixed') {
        const fixed = parseFloat(promoData.amount || promoData.discount_amount || '0');
        if (isNaN(fixed) || fixed <= 0) return 0;
        const disc = Math.round(fixed);
        return Math.min(disc, base);
      }
      // fallback: use discount_amount if provided
      if (promoData.discount_amount) {
        const d = Math.round(parseFloat(promoData.discount_amount));
        return isNaN(d) ? 0 : d;
      }
    } catch (e) {
      return 0;
    }
    return 0;
  };

  const calculateFinalTotal = () => {
    const base = totalPrice + calculateDelivery();
    const discount = calculatePromoDiscount();
    return Math.max(0, base - discount);
  };

  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    setOrderError(null);

    if (items.length === 0) {
      setOrderError('Ваша корзина пуста');
      return;
    }

    const pendingErrors: Record<string, boolean> = {};
    let validationMessage: string | null = null;
    let firstErrorRef: React.MutableRefObject<HTMLElement | null> | null = null;
    const registerError = (key: string, message: string, ref?: React.MutableRefObject<HTMLElement | null>) => {
      pendingErrors[key] = true;
      if (!validationMessage) validationMessage = message;
      if (!firstErrorRef && ref) firstErrorRef = ref;
    };

    const trimmedName = customerName?.trim();
    const trimmedSurname = customerSurname?.trim();
    const trimmedEmail = customerEmail?.trim();
    const trimmedPhone = customerPhone?.trim();
    const trimmedStreet = streetInput?.trim();
    const trimmedHouse = houseInput?.trim();
    const trimmedFlat = flatInput?.trim();

    if (!trimmedName) registerError('customerName', 'Заполните контактные данные (имя, фамилия, email, телефон)', contactInfoRef);
    if (!trimmedSurname) registerError('customerSurname', 'Заполните контактные данные (имя, фамилия, email, телефон)', contactInfoRef);
    if (!trimmedEmail) registerError('customerEmail', 'Заполните контактные данные (имя, фамилия, email, телефон)', contactInfoRef);
    if (!trimmedPhone) registerError('customerPhone', 'Заполните контактные данные (имя, фамилия, email, телефон)', contactInfoRef);

    if (!agreeTerms) registerError('agreeTerms', 'Подтвердите согласие с условиями и политикой конфиденциальности', termsRef);
    if (!city) registerError('city', 'Выберите город доставки', citySectionRef);
    if (!deliveryMethod) registerError('delivery', 'Выберите способ доставки (ПВЗ или курьер)', deliverySectionRef);

    if (deliveryMethod === 'cdek') {
      if (!selectedTariffCode) {
        registerError('cdekTariff', 'Выберите тариф доставки СДЭК', deliverySectionRef);
      }
      if (selectedTariffIsPvz && !selectedPVZUuid) {
        registerError('pvz', 'Выберите пункт выдачи заказа', pvzSectionRef);
      }
      if (selectedTariffIsDoor) {
        if (!trimmedStreet) registerError('streetInput', 'Укажите улицу для курьерской доставки', addressSectionRef);
        if (!trimmedHouse) registerError('houseInput', 'Укажите дом для курьерской доставки', addressSectionRef);
        if (!trimmedFlat) registerError('flatInput', 'Укажите квартиру или офис', addressSectionRef);
      }
    }

    if (Object.keys(pendingErrors).length > 0) {
      setFieldErrors(pendingErrors);
      setOrderError(validationMessage || 'Исправьте ошибки формы');
      if (firstErrorRef?.current) {
        firstErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setFieldErrors({});

    try {
      // push begin checkout event
      try {
        const ecomItems = items.map(i => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity, category: i.category }));
        // import dynamically to avoid circular deps at module load
        const { pushBeginCheckout } = await import('../services/ecommerce');
        pushBeginCheckout(ecomItems, 1);
      } catch (e) {
        console.debug('ecom begin checkout push failed', e);
      }
      // build shipping_address from available parts
      const shippingAddress = [city, street ? street : null, house ? `д ${house}` : null, flat ? `кв ${flat}` : null]
        .filter(Boolean).join(', ');

      // Determine shipping cost and method (use selected CDEK tariff if available)
      let shippingCostValue = calculateDelivery();
      let shippingMethodValue: string | undefined = deliveryMethod;
      if (deliveryMethod === 'cdek' && selectedTariffCode && Array.isArray(filteredCdekTariffs)) {
        const sel = filteredCdekTariffs.find((t: any) => Number(t.tariff_code) === Number(selectedTariffCode));
        if (sel) {
          shippingCostValue = Math.round(sel.delivery_sum || shippingCostValue);
          shippingMethodValue = `cdek_${sel.tariff_code}`;
        }
      }

      // If PVZ selected, attach PVZ metadata
      let pvzMeta: any = undefined;
  if (deliveryMethod === 'cdek' && selectedTariffIsPvz && selectedPVZUuid && Array.isArray(deliveryPointsAll)) {
    const pvz = deliveryPointsAll.find(d => (d.uuid && d.uuid === selectedPVZUuid) || (d.code && String(d.code) === String(selectedPVZUuid)));
        if (pvz) {
          pvzMeta = {
            pvz_uuid: pvz.uuid,
            pvz_code: pvz.code,
            pvz_name: pvz.name,
            pvz_address: pvz.address_full || pvz.address || null,
            pvz_location: pvz.location || null,
          };
        }
      }

      const payload: any = {
        customer_name: `${customerName} ${customerSurname}`,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        shipping_address: shippingAddress || undefined,
        // shipping info
        shipping_cost: shippingCostValue.toFixed(2),
        shipping_method: shippingMethodValue,
        promo_code: promoApplied && promoData && promoData.valid ? promoCode : undefined,
        items: items.map(i => ({ product_id: i.id, quantity: i.quantity }))
      };

      // If anonymous user wants to create account during checkout, tell backend
      if (createAccount && !isAuthenticated) {
        // backend expects create_user: true
        // attach only when requested
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        payload.create_user = true;
      }

      // Always indicate delivery_type when using CDEK
      if (deliveryMethod === 'cdek') {
        payload.delivery_type = 'cdek';
      }

      // Include selected CDEK tariff code if available
      if (selectedTariffCode) {
        payload.cdek_tariff_code = Number(selectedTariffCode);
      }

      // If PVZ selected, attach top-level pvz_code for backend convenience and also include pvz object
      if (pvzMeta) {
        payload.pvz = pvzMeta;
        if (pvzMeta.pvz_code) payload.pvz_code = pvzMeta.pvz_code;
        else if (pvzMeta.code) payload.pvz_code = pvzMeta.code;
      }

      // Include comment if provided
      if (comment && String(comment).trim()) {
        payload.comment = String(comment).trim();
      }

      // Include detailed address fields for door deliveries
      if (selectedTariffIsDoor) {
        // Backend expects separate delivery_* fields for door delivery
        if (city) payload.delivery_city = String(city).trim();
        if (streetInput) payload.delivery_street = String(streetInput).trim();
        // Keep house field exactly as the user entered (do not strip prefixes)
        if (houseInput) payload.delivery_house = String(houseInput).trim();
        if (flatInput) payload.delivery_apartment = String(flatInput).trim();
        if (floorInput) payload.delivery_floor = String(floorInput).trim();
        if (intercomCodeInput) payload.delivery_intercom = String(intercomCodeInput).trim();
        if (addressCommentInput) payload.delivery_address_comment = String(addressCommentInput).trim();

        // keep FIAS identifiers if available (useful for backend geocoding)
        if (selected_street_fias) payload.street_fias = selected_street_fias;
        if (selected_house_fias) payload.house_fias = selected_house_fias;
      }

      // If user is authenticated, include user_id in payload (try several common id fields)
      if ((typeof isAuthenticated !== 'undefined') && isAuthenticated && (typeof user !== 'undefined') && user) {
        const uid = (user.id || user.user_id || user.pk || user.uuid || null);
        if (uid) payload.user_id = uid;
      }

      // create order
      const resp = await api.post('/orders/', payload, { headers: { 'Content-Type': 'application/json' } });
      const data = resp.data;
      if (!data || !data.order_id) {
        setOrderError('Не удалось создать заказ. Попробуйте ещё раз.');
        return;
      }

      // store order id
      setOrderId(data.order_id);
      try {
        localStorage.setItem('last_order_id', data.order_id);
      } catch (e) {
        // ignore
      }

      // init payment
      const payResp = await api.post('/payments/init/', { order_id: data.order_id }, { headers: { 'Content-Type': 'application/json' } });
      const payData = payResp.data;
      if (payData && payData.redirectUrl) {
        // redirect user to bank
        window.location.href = payData.redirectUrl;
        return;
      }

      // if no redirect, mark submitted and show confirmation
      setOrderSubmitted(true);
      // push purchase event
      try {
        const { pushPurchase } = await import('../services/ecommerce');
        const revenue = calculateFinalTotal();
        pushPurchase(data.order_id, revenue, items.map(i => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity, category: i.category })));
      } catch (e) {
        console.debug('ecom purchase push failed', e);
      }
    } catch (err: any) {
      console.error('Order creation failed', err);
      const dataErr = err?.response?.data;
      // Special handling when backend reports insufficient stock
      if (dataErr && dataErr.error === 'not_enough_stock' && Array.isArray(dataErr.items)) {
        setStockIssues(dataErr.items);
        setStockModalOpen(true);
        return; // do not set generic order error
      }
      const msg = dataErr?.message || 'Ошибка при создании заказа';
      setOrderError(msg);
    }
  };

  // Handle quantity change for items in the order summary
  const handleQuantityChange = (id: number, change: number) => {
    const item = items.find(item => item.id === id);
    if (item) {
      updateQuantity(id, Math.max(1, item.quantity + change));
    }
  };

  // Auth state: auto-fill contact fields when user is logged in
  const { isAuthenticated, user } = useAuth();

  React.useEffect(() => {
    if (!isAuthenticated || !user) return;
    // Prefer explicit first_name/last_name fields, fall back to splitting `name`.
    const first = (user.first_name || (user as any).firstName) || ((user.name || '').split(/\s+/)[0] || '');
    const last = (user.last_name || (user as any).lastName) || ((user.name || '').split(/\s+/).slice(1).join(' ') || '');
    if (first) setCustomerName(first);
    if (last) setCustomerSurname(last);
    if (user.email) setCustomerEmail(user.email);
    if (user.phone) setCustomerPhone(user.phone);
  }, [isAuthenticated, user]);

  // Persist checkout form in sessionStorage to avoid losing data when user navigates away
  const STORAGE_KEY = 'checkout_form_v1';

  // Load persisted values on mount
  React.useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      // restore primitive fields if present
      if (saved.deliveryMethod) setDeliveryMethod(saved.deliveryMethod);
      if (saved.paymentMethod) setPaymentMethod(saved.paymentMethod);
      if (typeof saved.agreeTerms === 'boolean') setAgreeTerms(saved.agreeTerms);
      if (typeof saved.createAccount === 'boolean') setCreateAccount(saved.createAccount);
      if (saved.promoCode) setPromoCode(saved.promoCode);
      if (typeof saved.promoApplied === 'boolean') setPromoApplied(saved.promoApplied);
      if (saved.city) setCity(saved.city);
      if (saved.street) setStreet(saved.street);
      if (saved.house) setHouse(saved.house);
      if (saved.flat) setFlat(saved.flat);
      if (saved.postalCode) setPostalCode(saved.postalCode || '');
      if (saved.customerName) setCustomerName(saved.customerName);
      if (saved.customerSurname) setCustomerSurname(saved.customerSurname);
      if (saved.customerEmail) setCustomerEmail(saved.customerEmail);
      if (saved.customerPhone) setCustomerPhone(saved.customerPhone);
      if (saved.comment) setComment(saved.comment);
      if (saved.selectedTariffCode) setSelectedTariffCode(saved.selectedTariffCode);
      if (saved.selectedPVZUuid) setSelectedPVZUuid(saved.selectedPVZUuid);
      if (saved.streetInput) setStreetInput(saved.streetInput);
      if (saved.houseInput) setHouseInput(saved.houseInput);
      if (saved.flatInput) setFlatInput(saved.flatInput);
      if (saved.floorInput) setFloorInput(saved.floorInput);
      if (saved.intercomCodeInput) setIntercomCodeInput(saved.intercomCodeInput);
      if (saved.addressCommentInput) setAddressCommentInput(saved.addressCommentInput);
      if (saved.cityKladr) setCityKladr(saved.cityKladr);
      if (saved.cityCdekCode) setCityCdekCode(saved.cityCdekCode);
      if (saved.selected_street_fias) setSelectedStreetFias(saved.selected_street_fias);
      if (saved.selected_house_fias) setSelectedHouseFias(saved.selected_house_fias);
    } catch (e) {
      // ignore parse errors
      // eslint-disable-next-line no-console
      console.debug('No persisted checkout form or parse failed', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save relevant fields to sessionStorage when they change
  React.useEffect(() => {
    try {
      const toSave: any = {
        deliveryMethod,
        paymentMethod,
        agreeTerms,
        createAccount,
        promoCode,
        promoApplied,
        city,
        street,
        house,
        flat,
        postalCode,
        customerName,
        customerSurname,
        customerEmail,
        customerPhone,
        comment,
        selectedTariffCode,
        selectedPVZUuid,
        streetInput,
        houseInput,
        flatInput,
        floorInput,
        intercomCodeInput,
        addressCommentInput,
        cityKladr,
        cityCdekCode,
        selected_street_fias,
        selected_house_fias,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (e) {
      // ignore storage errors (quota/private mode)
    }
  }, [deliveryMethod, paymentMethod, agreeTerms, createAccount, promoCode, promoApplied, city, street, house, flat, postalCode, customerName, customerSurname, customerEmail, customerPhone, comment, selectedTariffCode, selectedPVZUuid, streetInput, houseInput, flatInput, floorInput, intercomCodeInput, addressCommentInput, cityKladr, cityCdekCode, selected_street_fias, selected_house_fias]);

  // Clear persisted data upon successful order submission
  React.useEffect(() => {
    if (orderSubmitted) {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
    }
  }, [orderSubmitted]);

  // Stock modal actions
  const handleReduceAllToAvailable = async () => {
    if (!Array.isArray(stockIssues)) return;
    setStockActionLoading(true);
    try {
      stockIssues.forEach((it) => {
        // find cart item id by product_id
        const pid = Number(it.product_id || it.id || it.productId);
        if (!pid) return;
        const available = Number(it.available || 0);
        if (available > 0) {
          updateQuantity(pid, Math.max(1, available));
        } else {
          // remove items with zero availability
          removeItem(pid);
        }
      });
      // close modal after adjustments
      setStockModalOpen(false);
      setStockIssues(null);
    } catch (e) {
      console.error('Failed to apply stock adjustments', e);
    } finally {
      setStockActionLoading(false);
    }
  };

  const handleRemoveUnavailable = async () => {
    if (!Array.isArray(stockIssues)) return;
    setStockActionLoading(true);
    try {
      stockIssues.forEach((it) => {
        const pid = Number(it.product_id || it.id || it.productId);
        const available = Number(it.available || 0);
        if (available <= 0 && pid) removeItem(pid);
      });
      setStockModalOpen(false);
      setStockIssues(null);
    } catch (e) {
      console.error('Failed to remove unavailable items', e);
    } finally {
      setStockActionLoading(false);
    }
  };

  const handleContinueWithCurrent = async () => {
    // Attempt to re-submit order with a soft flag indicating user agrees to proceed
    // Note: backend must support this flag (allow_backorder); if not, it may still fail.
    setStockActionLoading(true);
    try {
      const body = {
        // minimal info to attempt order: reuse existing form state by triggering submit again
        // we signal intent to allow backorder processing
        allow_backorder: true,
      };
      // Re-submit by calling existing submit handler path: emulate submit by posting to orders with same payload
      // For safety, we call the same endpoint but include the flag. The server may ignore it.
      // Reconstruct payload as minimal: use existing local state variables to build payload similar to original
      const payload: any = {
        customer_name: `${customerName} ${customerSurname}`,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        shipping_address: [city, street ? street : null, house ? `д ${house}` : null, flat ? `кв ${flat}` : null].filter(Boolean).join(', ') || undefined,
        shipping_cost: calculateDelivery().toFixed(2),
        shipping_method: deliveryMethod,
        promo_code: promoApplied && promoData && promoData.valid ? promoCode : undefined,
        items: items.map(i => ({ product_id: i.id, quantity: i.quantity })),
        allow_backorder: true,
      };
      const resp = await api.post('/orders/', payload, { headers: { 'Content-Type': 'application/json' } });
      const data = resp.data;
      if (data && data.order_id) {
        setOrderId(data.order_id);
        setOrderSubmitted(true);
        try { localStorage.setItem('last_order_id', data.order_id); } catch (e) {}
        // push purchase event if available
        try {
          const { pushPurchase } = await import('../services/ecommerce');
          const revenue = calculateFinalTotal();
          pushPurchase(data.order_id, revenue, items.map(i => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity, category: i.category })));
        } catch (e) { /* ignore */ }
        setStockModalOpen(false);
        setStockIssues(null);
        return;
      }
      setOrderError('Не удалось оформить заказ с текущими количествами. Попробуйте изменить корзину.');
    } catch (err: any) {
      console.error('Continue with current quantities failed', err);
      const dataErr = err?.response?.data;
      setOrderError(dataErr?.message || 'Ошибка при оформлении заказа');
    } finally {
      setStockActionLoading(false);
    }
  };

  // when user decreased an item's quantity in the modal, allow them to retry submit
  const handleDecreaseToAvailable = async (pid: number, available: number) => {
    setStockActionLoading(true);
    try {
      const maybe = updateQuantity(pid, Math.max(1, Number(available)));
      if (maybe && typeof (maybe as any).then === 'function') await maybe;
      // do not directly toggle submit availability; it will be derived from cart state
    } catch (e) {
      console.error('Failed to update quantity for stock modal', e);
    } finally {
      setStockActionLoading(false);
    }
  };

  // remove item and close modal immediately
  const handleRemoveAndClose = (pid: number) => {
    try {
      removeItem(pid);
    } catch (e) {
      console.error('Failed to remove item from cart', e);
    }
    // remove the item from stockIssues list so the modal updates
    setStockIssues(prev => {
      if (!Array.isArray(prev)) return prev;
      const remaining = prev.filter(it => Number(it.product_id || it.id) !== Number(pid));
      // If no remaining issues, close modal
      if (remaining.length === 0) {
        setStockModalOpen(false);
        return null;
      }
      return remaining;
    });
  };

  // NOTE: automatic retry-on-adjust was removed per UX decision — user must close modal and review cart

  // determine whether the modal may be closed: true when there are no unresolved stock issues
  // or when for every reported issue the cart either no longer contains the item (removed)
  // or the cart item's quantity is <= available
  const canClose = React.useMemo(() => {
    if (!Array.isArray(stockIssues) || stockIssues.length === 0) return true;
    return stockIssues.every((it) => {
      const pid = Number(it.product_id || it.id || it.productId);
      const cartItem = items.find(i => Number(i.id) === pid);
      if (!cartItem) return true; // removed -> resolved
      const available = Number(it.available || 0);
      return Number(cartItem.quantity) <= available;
    });
  }, [stockIssues, items]);

  // Automatically close the stock modal when all issues become resolved (transition false -> true)
  const prevCanCloseRef = React.useRef<boolean>(canClose);
  React.useEffect(() => {
    // Only act on a transition from not-closable to closable while modal is open
    if (stockModalOpen && !stockActionLoading && prevCanCloseRef.current === false && canClose === true) {
      // close modal but do NOT attempt to re-submit the order automatically
      setStockModalOpen(false);
      setStockIssues(null);
    }
    prevCanCloseRef.current = canClose;
  }, [canClose, stockModalOpen, stockActionLoading]);

  // When the stock modal opens, fetch product thumbnails for better UX
  React.useEffect(() => {
    if (!stockModalOpen || !Array.isArray(stockIssues) || stockIssues.length === 0) return;
    // snapshot the issues so we don't trigger repeated fetch loops when we setStockIssues
    const initialIssues = stockIssues.slice();
    let cancelled = false;

    (async () => {
      try {
        const updated = await Promise.all(initialIssues.map(async (it) => {
          // try slug first, then product_id or id
          const pidOrSlug = it.slug || it.product_id || it.id;
          if (!pidOrSlug) return { ...it, thumb: null };
          try {
            const detail = await fetchProductDetail(pidOrSlug);
            const img = Array.isArray(detail.images) && detail.images.length > 0 ? detail.images[0] : (detail.main_image || detail.image || null);
            return { ...it, thumb: img || null };
          } catch (err) {
            // ignore per-item fetch errors
            return { ...it, thumb: null };
          }
        }));

        if (!cancelled) setStockIssues(updated as any[]);
      } catch (e) {
        console.error('Failed to fetch product thumbnails for stock modal', e);
      }
    })();

    return () => { cancelled = true; };
    // run only when modal opens (or closes)
  }, [stockModalOpen]);

  return (
    <section className="py-24 bg-background">
      {/* Stock issues modal (shown when backend returns not_enough_stock) */}
      <Modal isOpen={stockModalOpen} onClose={() => { setStockModalOpen(false); setStockIssues(null); }} size="lg" scrollBehavior="inside">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h3 className="text-xl">Некоторых товаров нет в нужном количестве</h3>
                <p className="text-sm text-foreground/70">Мы обнаружили, что часть товаров в вашей корзине недоступна в запрошенном количестве. Выберите действие:</p>
              </ModalHeader>
              <Divider />
              <ModalBody>
                {stockIssues && stockIssues.length > 0 ? (
                  <div className="space-y-4">
                    {stockIssues.map((it, idx) => (
                      <div key={idx} className="p-4 border rounded bg-content2/50">
                        <div className="flex items-start gap-4">
                          {/* thumbnail */}
                          <div className="w-16 h-16 bg-content2 flex-shrink-0 rounded overflow-hidden">
                            {it.thumb ? (
                              <img src={it.thumb} alt={it.name || it.slug || ''} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-content3 flex items-center justify-center text-sm text-foreground/60">Фото</div>
                            )}
                          </div>

                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="font-medium">{it.name || it.slug || `Товар ${it.product_id || ''}`}</div>
                                <div className="text-sm text-foreground/60">Артикул/slug: {it.slug || it.product_id}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-foreground/70">Запрошено: <span className="font-medium">{it.requested}</span></div>
                                <div className={`text-sm ${Number(it.available) <= 0 ? 'text-danger' : 'text-foreground/70'}`}>Доступно: <span className="font-medium">{it.available}</span></div>
                              </div>
                            </div>
                            <div className="mt-3 flex gap-2">
                              {Number(it.available) > 0 ? (
                                <Button size="sm" color="primary" onPress={async () => { await handleDecreaseToAvailable(Number(it.product_id || it.id), Number(it.available)); }} isDisabled={stockActionLoading}>
                                  Уменьшить до {it.available}
                                </Button>
                              ) : null}
                              <Button size="sm" color="danger" onPress={() => { handleRemoveAndClose(Number(it.product_id || it.id)); }} isDisabled={stockActionLoading}>
                                Удалить из корзины
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="p-4 bg-background border rounded">
                      <p className="text-sm text-foreground/70">К сожалению, часть товаров недоступна в запрошенном количестве. Чтобы оформить недостающие объёмы под заказ, пожалуйста, свяжитесь с нами по телефону <a href="tel:+79958810247" className="text-primary">+7 (995) 881-02-47</a> или по электронной почте <a href="mailto:info@safonova-brand.ru" className="text-primary">info@safonova-brand.ru</a>. Наш менеджер подскажет сроки и условия производства/доставки.</p>
                    </div>
                  </div>
                ) : (
                  <p>Нет данных о недоступных товарах.</p>
                )}
              </ModalBody>
              <ModalFooter>
                <div className="flex gap-2 w-full justify-end">
                  <Button variant="flat" color="default" onPress={() => { setStockModalOpen(false); setStockIssues(null); }} isDisabled={stockActionLoading || !canClose}>
                    Закрыть
                  </Button>
                </div>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <Helmet>
        <title>Оформление заказа — SAFONOVA</title>
        <meta name="description" content="Оформление заказа в SAFONOVA — укажите контактные данные, адрес доставки и способ оплаты." />
        <link rel="canonical" href={`${BASE_URL || ''}${location.pathname}${location.search || ''}`} />
        <meta property="og:title" content="Оформление заказа — SAFONOVA" />
        <meta property="og:description" content="Оформление заказа в SAFONOVA — укажите контактные данные, адрес доставки и способ оплаты." />
        <meta property="og:url" content={`${BASE_URL || ''}${location.pathname}${location.search || ''}`} />
      </Helmet>
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-light mb-4">Оформление <span className="font-medium">заказа</span></h1>
          <p className="text-foreground/70 max-w-2xl mx-auto">
            Заполните форму ниже для оформления вашего заказа
          </p>
        </div>

        {orderSubmitted ? (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-2xl mx-auto text-center py-16"
          >
            <div className="bg-primary/10 p-6 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-8">
              <Icon icon="lucide:check" className="text-primary" width={48} />
            </div>
            <h2 className="text-2xl font-medium mb-4">Спасибо за ваш заказ!</h2>
            <p className="text-foreground/80 mb-8">
              Ваш заказ успешно оформлен. Номер вашего заказа: <span className="font-medium">{orderId || clientOrderFallback || '—'}</span>.<br />
              Мы отправили подтверждение на ваш email. Наш менеджер свяжется с вами в ближайшее время.
            </p>
            <Button 
              color="primary" 
              size="lg"
              as="a"
              href="#"
            >
              Вернуться на главную
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {/* Show a message if cart is empty */}
              {items.length === 0 ? (
                <div className="text-center py-12">
                  <Icon icon="lucide:shopping-bag" className="mx-auto mb-4 text-foreground/40" width={48} />
                  <h3 className="text-xl font-medium mb-4">Ваша корзина пуста</h3>
                  <p className="text-foreground/70 mb-6">
                    Добавьте товары в корзину, чтобы оформить заказ
                  </p>
                  <Button 
                    color="primary"
                    size="lg"
                    as="a"
                    href="/catalog"
                  >
                    Перейти в каталог
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmitOrder}>
                  <div ref={contactInfoRef}>
                    <Card className={`border bg-content2/50 mb-8 ${hasContactFieldErrors ? 'border-danger' : 'border-divider'}`} disableRipple>
                      <CardBody className="p-8">
                        <h2 className="text-xl font-medium mb-6">Контактная информация</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <Input
                            type="text"
                            label="Имя"
                            placeholder="Введите ваше имя"
                            variant="bordered"
                            isRequired
                            value={customerName}
                            isInvalid={Boolean(fieldErrors.customerName)}
                            errorMessage={fieldErrors.customerName ? 'Укажите имя' : undefined}
                            onValueChange={(value) => {
                              setCustomerName(value);
                              if (value && value.trim()) clearFieldError('customerName');
                            }}
                          />
                          <Input
                            type="text"
                            label="Фамилия"
                            placeholder="Введите вашу фамилию"
                            variant="bordered"
                            isRequired
                            value={customerSurname}
                            isInvalid={Boolean(fieldErrors.customerSurname)}
                            errorMessage={fieldErrors.customerSurname ? 'Укажите фамилию' : undefined}
                            onValueChange={(value) => {
                              setCustomerSurname(value);
                              if (value && value.trim()) clearFieldError('customerSurname');
                            }}
                          />
                          <Input
                            type="email"
                            label="Email"
                            placeholder="Введите ваш email"
                            variant="bordered"
                            isRequired
                            value={customerEmail}
                            isInvalid={Boolean(fieldErrors.customerEmail)}
                            errorMessage={fieldErrors.customerEmail ? 'Укажите email' : undefined}
                            onValueChange={(value) => {
                              setCustomerEmail(value);
                              if (value && value.trim()) clearFieldError('customerEmail');
                            }}
                          />
                          <Input
                            type="tel"
                            label="Телефон"
                            placeholder="Введите ваш телефон"
                            variant="bordered"
                            isRequired
                            value={customerPhone}
                            isInvalid={Boolean(fieldErrors.customerPhone)}
                            errorMessage={fieldErrors.customerPhone ? 'Укажите телефон' : undefined}
                            onValueChange={(value) => {
                              setCustomerPhone(value);
                              if (value && value.trim()) clearFieldError('customerPhone');
                            }}
                          />
                        </div>
                      </CardBody>
                    </Card>
                  </div>

                  <div ref={citySectionRef}>
                    <Card className={`border bg-content2/50 mb-8 ${fieldErrors.city ? 'border-danger' : 'border-divider'}`} disableRipple>
                      <CardBody className="p-8">
                        <h2 className="text-xl font-medium mb-6">Город</h2>
                        <div className="space-y-6">
                          <div>
                            <label className="text-sm text-foreground/70 mb-2 block">Город</label>
                            <AddressAutocomplete
                              type="city"
                              placeholder="Начните вводить город"
                              // use address suggest city endpoint per contract
                              suggestUrl="/address/suggest/city/"
                              initialValue={city}
                              onSelect={async (s: any) => {
                              // extract display and identifiers (support many provider shapes)
                              const full = s.raw?.full_name || s._raw?.full_name || s.value || s.name || '';
                              setCity(full);
                              if (full) clearFieldError('city');

                              // attempt a broad set of kladr/fallback keys we might receive
                              const kladrCandidates = [
                                s.raw?.city_kladr_id,
                                s.raw?.city_kladr,
                                s.raw?.kladr,
                                s._raw?.data?.kladr,
                                s.raw?.data?.kladr,
                                s.data?.kladr,
                                s.data?.city_kladr_id,
                                s.kladr,
                                s.city_kladr_id,
                                s.value && null,
                              ];
                              let kladr = kladrCandidates.find((x: any) => x !== undefined && x !== null) || null;

                              // Fallback: try to extract FIAS if kladr missing
                              const fias = s.raw?._raw?.data?.fias || s._raw?.data?.fias || s.raw?.data?.fias || s.data?.fias || s.fias || null;
                              setCityKladr(kladr || null);
                              setSelectedCityFias(fias || null);

                              // reset any previously selected delivery info
                              setCityCdekUuid(null);
                              setCityCdekCode(null);
                              setCdekTariffs(null);
                              setSelectedTariffCode(null);
                              setDeliveryMethod(undefined);
                              clearFieldError('delivery');
                              clearFieldError('cdekTariff');

                              // Debug: log suggestion and extracted identifiers
                              console.debug('City selected', { suggestion: s, extracted: { kladr, fias } });

                              // Ask backend to resolve delivery capabilities for selected city
                              try {
                                // if no kladr found, attempt to use fias as kladr fallback (some backends accept it)
                                const resolveKey = kladr || fias || null;
                                if (resolveKey) {
                                  console.debug('Calling delivery resolve with kladr=', resolveKey);
                                  const resp = await api.post('/address/delivery/resolve/', { kladr: resolveKey }, { headers: { 'Content-Type': 'application/json' } });
                                  const data = resp?.data;
                                  // backend returns cdek_id when available (location code or numeric id)
                                  if (data && data.cdek_id) {
                                    // store CDEK id/code and let existing tariff effect fetch tarifs
                                    setCityCdekCode(data.cdek_id);
                                    setCityCdekUuid(data.cdek_uuid || null);
                                    setCdekError(null);
                                  } else {
                                    // CDEK not available for this city
                                    setCdekTariffs([]);
                                    setCdekError('СДЭК недоступен для этого города — свяжитесь с нами для оформления доставки.');
                                  }
                                } else {
                                  // no kladr/fias — cannot resolve
                                  console.warn('No kladr or fias available from suggestion, skipping delivery resolve');
                                  setCdekTariffs([]);
                                  setCdekError('Не удалось определить код города для доставки');
                                }
                              } catch (err: any) {
                                console.error('Delivery resolve error', err);
                                setCdekTariffs([]);
                                setCdekError(err?.response?.data?.message || 'Ошибка при определении доступных служб доставки');
                              }
                              }}
                            />
                          {fieldErrors.city && <p className="text-danger text-sm mt-2">Выберите город доставки</p>}
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </div>

                  {city ? (
                    <div ref={deliverySectionRef}>
                      <Card className={`border bg-content2/50 mb-8 ${hasDeliveryErrors ? 'border-danger' : 'border-divider'}`} disableRipple>
                        <CardBody className="p-8">
                          <h2 className="text-xl font-medium mb-6">Способ доставки</h2>
                          <div>
                            <div className="mb-3 text-foreground/70">СДЭК — варианты и цены</div>
                            {cdekLoading ? (
                              <div className="text-sm text-foreground/60">Загрузка тарифов...</div>
                            ) : cdekError ? (
                              <div className="text-sm text-danger">{cdekError}</div>
                            ) : (!filteredCdekTariffs || filteredCdekTariffs.length === 0) ? (
                              <div className="text-sm text-foreground/60">Тарифы не найдены для выбранного города</div>
                            ) : (
                              <div className="grid grid-cols-1 gap-3">
                                {filteredCdekTariffs.map((t: any) => {
                                const rawTariffName = String(t.tariff_name || '').trim();
                                const normalizedTariffName = rawTariffName.toLowerCase();
                                const displayName = normalizedTariffName === 'посылка склад-дверь'
                                  ? 'СДЭК Курьер'
                                  : normalizedTariffName === 'посылка склад-склад'
                                    ? 'СДЭК ПВЗ'
                                    : rawTariffName || 'Тариф СДЭК';

                                return (
                                  <div key={t.tariff_code} className={`p-4 border rounded-medium flex items-center justify-between cursor-pointer ${selectedTariffCode === t.tariff_code ? 'border-primary/60 bg-primary/5' : 'border-divider bg-transparent'}`} onClick={() => {
                                      // select tariff and set delivery method; clear opposite inputs based on tariff name
                                      setSelectedTariffCode(t.tariff_code);
                                      setDeliveryMethod('cdek');
                                      clearFieldError('delivery');
                                      clearFieldError('cdekTariff');
                                      const name = String(t.tariff_name || '').toLowerCase();
                                      const isDoor = name.includes('двер');
                                      const isPvz = name.includes('склад') && !isDoor; // prefer door when both present
                                      if (isPvz) {
                                        // PVZ: clear door address inputs and fias
                                        setStreetInput(''); setHouseInput(''); setFlatInput('');
                                        setSelectedCityFias(null); setSelectedStreetFias(null); setSelectedHouseFias(null);
                                        setStreetSuggestions([]); setHouseSuggestions([]);
                                        // reset cached PVZ list and selection to allow fresh selection/fetch
                                        setDeliveryPointsAll(null); setDeliveryPoints(null); setSelectedPVZUuid(null);
                                        clearFieldError('streetInput');
                                        clearFieldError('houseInput');
                                        clearFieldError('flatInput');
                                      } else if (isDoor) {
                                        // Door: clear PVZ selection
                                        setSelectedPVZUuid(null);
                                        setDeliveryPoints(null);
                                        clearFieldError('pvz');
                                      }
                                    }}>
                                    <div className="flex items-center gap-3">
                                      <div className={`w-6 h-6 rounded-full border flex items-center justify-center ${selectedTariffCode === t.tariff_code ? 'bg-primary text-white border-primary' : 'bg-white border-foreground/20'}`}>
                                        {selectedTariffCode === t.tariff_code ? <Icon icon="lucide:check" width={14} /> : null}
                                      </div>
                                      <div>
                                          <div className="font-medium">{displayName} <span className="text-foreground/70 text-sm ml-2">{typeof t.calendar_min !== 'undefined' && typeof t.calendar_max !== 'undefined' ? `${t.calendar_min}-${t.calendar_max} дня` : ''}</span></div>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="font-medium">{Math.round(t.delivery_sum)} ₽</div>
                                    </div>
                                  </div>
                                );
                              })}
                              </div>
                            )}
                            {(fieldErrors.delivery || fieldErrors.cdekTariff) && (
                              <div className="text-danger text-sm mt-4 space-y-1">
                                {fieldErrors.delivery && <p>Выберите способ доставки</p>}
                                {fieldErrors.cdekTariff && <p>Выберите тариф доставки СДЭК</p>}
                              </div>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  ) : (
                    <div className="mb-4 text-foreground/60">Выберите город, чтобы увидеть доступные способы доставки.</div>
                  )}

                  {/* If selected tariff is 'до двери' show street/house/flat inputs */}
                  {selectedTariffIsDoor && (
                    <div className="relative mb-8" ref={addressSectionRef}>
                      <Card className={`border bg-content2/50 ${hasAddressErrors ? 'border-danger' : 'border-divider'}`} disableRipple>
                        <CardBody className="p-8">
                          <h2 className="text-xl font-medium mb-6">Адрес доставки — до двери</h2>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="relative">
                              <Input
                                type="text"
                                label="Улица"
                                placeholder="Начните вводить улицу"
                                variant="bordered"
                                value={streetInput}
                                isInvalid={Boolean(fieldErrors.streetInput)}
                                errorMessage={fieldErrors.streetInput ? 'Укажите улицу' : undefined}
                                onValueChange={handleStreetInputChange}
                                onFocus={() => setShowStreetSuggestions(streetSuggestions.length > 0)}
                                onBlur={() => setTimeout(() => setShowStreetSuggestions(false), 150)}
                              />
                              {/* Street suggestions dropdown attached to field */}
                              {showStreetSuggestions && streetSuggestions.length > 0 && (
                                <div className="absolute left-0 top-full mt-2 w-full z-50">
                                  <div className="bg-background border border-divider rounded shadow-lg max-h-64 overflow-auto">
                                    {streetSuggestions.map((sugg: any, idx: number) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => handleSelectStreetSuggestion(sugg)}
                                        className="w-full text-left px-4 py-2 hover:bg-content3"
                                      >
                                        <div className="text-sm font-medium">{getSuggestionDisplay(sugg)}</div>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="relative">
                              <Input
                                type="text"
                                label="Дом"
                                placeholder="Номер дома"
                                variant="bordered"
                                value={houseInput}
                                isInvalid={Boolean(fieldErrors.houseInput)}
                                errorMessage={fieldErrors.houseInput ? 'Укажите дом' : undefined}
                                onValueChange={handleHouseInputChange}
                                onFocus={() => setShowHouseSuggestions(houseSuggestions.length > 0)}
                                onBlur={() => setTimeout(() => setShowHouseSuggestions(false), 150)}
                              />
                              {/* House suggestions dropdown attached to field */}
                              {showHouseSuggestions && houseSuggestions.length > 0 && (
                                <div className="absolute left-0 top-full mt-2 w-full z-50">
                                  <div className="bg-background border border-divider rounded shadow-lg max-h-64 overflow-auto">
                                    {houseSuggestions.map((sugg: any, idx: number) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => handleSelectHouseSuggestion(sugg)}
                                        className="w-full text-left px-4 py-2 hover:bg-content3"
                                      >
                                        <div className="text-sm font-medium">{getSuggestionDisplay(sugg)}</div>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            <Input
                              type="text"
                              label="Квартира/офис"
                              placeholder="Квартира / офис"
                              variant="bordered"
                              value={flatInput}
                              isInvalid={Boolean(fieldErrors.flatInput)}
                              errorMessage={fieldErrors.flatInput ? 'Укажите квартиру или офис' : undefined}
                              onValueChange={(value) => {
                                setFlatInput(value);
                                if (value && value.trim()) clearFieldError('flatInput');
                              }}
                            />
                            <Input
                              type="text"
                              label="Этаж"
                              placeholder="Этаж (опционально)"
                              variant="bordered"
                              value={floorInput}
                              onValueChange={setFloorInput}
                            />
                            <Input
                              type="text"
                              label="Код домофона"
                              placeholder="Код домофона (опционально)"
                              variant="bordered"
                              value={intercomCodeInput}
                              onValueChange={setIntercomCodeInput}
                            />
                            <Input
                              type="text"
                              label="Комментарий к адресу"
                              placeholder="Комментарий к адресу (опционально)"
                              variant="bordered"
                              value={addressCommentInput}
                              onValueChange={setAddressCommentInput}
                            />
                          </div>
                          {addrLoading && <p className="text-sm text-foreground/60 mt-3">Определение адреса...</p>}
                          {addrError && <p className="text-sm text-danger mt-3">{addrError}</p>}
                        </CardBody>
                      </Card>

                      {/* Note: per-field dropdowns are rendered inline under each Input above; removed the duplicate overlays that were rendered above the Card */}
                    </div>
                  )}

                    {/* If selected tariff is PVZ show pickup points list */}
                    {selectedTariffIsPvz && (
                      <div ref={pvzSectionRef}>
                        <Card className={`border bg-content2/50 mb-8 ${fieldErrors.pvz ? 'border-danger' : 'border-divider'}`} disableRipple>
                          <CardBody className="p-8">
                            <h2 className="text-xl font-medium mb-6">Выберите ПВЗ</h2>
                            <div>
                              <div className="mb-3">
                                <Input
                                  type="text"
                                  label="Поиск ПВЗ"
                                  placeholder="Начните вводить адрес или код ПВЗ — список появится ниже"
                                  variant="bordered"
                                  value={pvzFilterText}
                                  isInvalid={Boolean(fieldErrors.pvz)}
                                  errorMessage={fieldErrors.pvz ? 'Выберите пункт выдачи' : undefined}
                                  onValueChange={setPvzFilterText}
                                  onFocus={() => setPvzFocused(true)}
                                  onBlur={() => setTimeout(() => setPvzFocused(false), 150)}
                                />
                              </div>
                            <div className="relative">
                              <>
                                {deliveryPointsLoading && <div className="text-sm text-foreground/60">Загрузка списка ПВЗ...</div>}
                                {deliveryPointsError && <div className="text-sm text-danger">{deliveryPointsError}</div>}
                                {pvzFocused && (
                                  <>
                                    {!deliveryPointsLoading && Array.isArray(deliveryPointsAll) && displayDeliveryPoints.length > 0 && (
                                      <div className="bg-content2/50 border border-divider rounded shadow-sm max-h-80 overflow-auto">
                                        {displayDeliveryPoints.map((p: any) => {
                                          const isSel = selectedPVZUuid && (selectedPVZUuid === (p.uuid || p.code));
                                          return (
                                            <button
                                              key={p.uuid || p.code}
                                              type="button"
                                              onClick={() => handleSelectPVZ(p)}
                                              className={`w-full text-left px-4 py-3 hover:bg-content3 flex justify-between items-start ${isSel ? 'bg-primary/5 border-l-4 border-primary' : ''}`}
                                            >
                                              <div>
                                                <div className="font-medium text-white">{p.location?.address || p.address || p.address_full || p.name || ''}</div>
                                                {p.work_time && <div className="text-sm text-foreground/60 mt-1">{p.work_time}</div>}
                                              </div>
                                              <div className="text-sm text-foreground/60 ml-4">{p.code}</div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                    {!deliveryPointsLoading && Array.isArray(deliveryPointsAll) && displayDeliveryPoints.length === 0 && (
                                      <div className="text-sm text-foreground/60">ПВЗ не найдены</div>
                                    )}
                                  </>
                                )}
                                {selectedPVZUuid && (() => {
                                  const pvz = (deliveryPointsAll || []).find((d: any) => (d.uuid && d.uuid === selectedPVZUuid) || (d.code && String(d.code) === String(selectedPVZUuid)));
                                  if (!pvz) return null;
                                  return (
                                    <div className="mt-4 p-3 border rounded bg-primary/5">
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <div className="font-medium text-white mb-1">{pvz.location?.address || pvz.address || pvz.address_full || pvz.name || ''}</div>
                                          {pvz.work_time && <div className="text-sm text-foreground/60 mt-1">{pvz.work_time}</div>}
                                        </div>
                                        <div className="text-sm text-foreground/60 ml-4">{pvz.code}</div>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </>
                            </div>
                              {fieldErrors.pvz && !selectedPVZUuid && (
                                <p className="text-danger text-sm mt-4">Выберите пункт выдачи для продолжения</p>
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      </div>
                    )}

                  <Card className="border border-divider bg-content2/50 mb-8" disableRipple>
                    <CardBody className="p-8">
                      <h2 className="text-xl font-medium mb-6">Способ оплаты</h2>
                      <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                        <div className="space-y-4">
                          <div className="flex items-center p-4 border border-divider rounded-medium">
                            <Radio value="card_online">Банковской картой или СБП онлайн</Radio>
                          </div>
                        </div>
                      </RadioGroup>
                    </CardBody>
                  </Card>

                  {/* Promo code block */}
                  <Card className="border border-divider bg-content2/50 mb-8" disableRipple>
                    <CardBody className="p-8">
                      <h2 className="text-xl font-medium mb-4">Промокод</h2>
                      <div className="flex gap-3 items-start">
                        <Input
                          value={promoCode}
                          onValueChange={setPromoCode}
                          placeholder="Введите промокод"
                          variant="bordered"
                          className="flex-grow"
                        />
                        <Button
                          color="primary"
                          onPress={async () => {
                            setPromoMessage(null);
                            setPromoData(null);
                            setPromoApplied(false);
                            const code = (promoCode || '').trim();
                            if (!code) {
                              setPromoMessage('Введите промокод');
                              return;
                            }
                            try {
                              // subtotal до применения скидки — только товары, без доставки
                              const subtotalValue = (totalPrice).toFixed(2);
                              const resp = await api.post('/orders/validate-promo/', {
                                promo_code: code,
                                subtotal: subtotalValue,
                              }, {
                                headers: { 'Content-Type': 'application/json' }
                              });
                              const data = resp.data;
                              if (!data || data.valid !== true) {
                                const msg = data?.message || 'Промокод недействителен';
                                setPromoMessage(msg);
                                setPromoData(data || { valid: false, message: msg });
                                setPromoApplied(false);
                                return;
                              }
                              // valid
                              setPromoData(data);
                              setPromoApplied(true);
                              setPromoMessage(data.message || 'Промокод применён');
                            } catch (err: any) {
                              console.error('Promo validation error', err);
                              const msg = err?.response?.data?.message || 'Ошибка проверки промокода';
                              setPromoMessage(msg);
                              setPromoData({ valid: false, message: msg });
                              setPromoApplied(false);
                            }
                          }}
                        >
                          Применить
                        </Button>
                      </div>
                      {promoMessage && (
                        <p className={`mt-3 text-sm ${promoApplied ? 'text-success' : 'text-danger'}`}>{promoMessage}</p>
                      )}
                    </CardBody>
                  </Card>

                  <Card className="border border-divider bg-content2/50 mb-8" disableRipple>
                    <CardBody className="p-8">
                      <h2 className="text-xl font-medium mb-6">Комментарий к заказу</h2>
                      <Textarea
                        value={comment}
                        onValueChange={setComment}
                        placeholder="Дополнительная информация к заказу"
                        variant="bordered"
                        minRows={4}
                      />
                    </CardBody>
                  </Card>

                  <div
                    ref={termsRef}
                    className={`mb-8 p-4 rounded-medium border ${fieldErrors.agreeTerms ? 'border-danger bg-danger/10' : 'border-divider bg-content2/50'}`}
                  >
                    <Checkbox 
                      isSelected={agreeTerms}
                      onValueChange={(value) => {
                        setAgreeTerms(value);
                        if (value) clearFieldError('agreeTerms');
                      }}
                      isRequired
                      isInvalid={Boolean(fieldErrors.agreeTerms)}
                    >
                      <span className="text-sm">
                        Я согласен с <Link href="#" className="text-primary">публичной офертой</Link> и <Link href="#" className="text-primary">политикой конфиденциальности</Link>
                      </span>
                    </Checkbox>
                    {fieldErrors.agreeTerms && (
                      <p className="text-danger text-sm mt-3">Подтвердите согласие с условиями и политикой конфиденциальности</p>
                    )}
                  </div>

                  {/* If user is anonymous, offer creating an account */}
                  {!isAuthenticated && (
                    <div className="mb-8 p-4 rounded-medium border border-divider bg-content2/50">
                      <Checkbox
                        isSelected={createAccount}
                        onValueChange={setCreateAccount}
                      >
                        <span className="text-sm">Создать учетную запись покупателя</span>
                      </Checkbox>
                    </div>
                  )}

                  {(orderError || addressError) && (
                    <div className="mb-4">
                      {orderError && <p className="text-danger text-sm mb-2">{orderError}</p>}
                      {addressError && <p className="text-danger text-sm">{addressError}</p>}
                    </div>
                  )}

                  <Button 
                    type="submit"
                    color="primary" 
                    size="lg"
                    className="w-full font-medium"
                    isDisabled={!agreeTerms || items.length === 0}
                  >
                    Оформить заказ
                  </Button>
                </form>
              )}
            </div>

            {/* Order Summary */}
            <div>
              <Card className="border border-divider bg-content2/50 sticky top-24" disableRipple>
                <CardBody className="p-6">
                  <h2 className="text-xl font-medium mb-6">Ваш заказ</h2>
                  
                  {items.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-foreground/70">Корзина пуста</p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-6 mb-6">
                        {items.map((item) => (
                          <div key={item.id} className="border-b border-divider pb-4 last:border-none">
                            <div className="flex gap-4">
                              <div className="w-16 h-16 bg-content2 flex-shrink-0">
                                <img 
                                  src={item.image} 
                                  alt={item.name} 
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-grow">
                                <div className="flex justify-between">
                                  <div>
                                    <h4 className="font-medium">{item.name}</h4>
                                    <p className="text-sm text-foreground/70">{item.category}</p>
                                  </div>
                                  <Button 
                                    isIconOnly 
                                    size="sm" 
                                    variant="default" 
                                    onPress={() => removeItem(item.id)}
                                  >
                                    <Icon icon="lucide:x" className="text-red-600" width={16} />
                                  </Button>
                                </div>
                                
                                <div className="flex justify-between items-center mt-2 flex-wrap gap-2">
                                  <div className="flex items-center border border-divider">
                                    <Button 
                                      isIconOnly 
                                      size="sm" 
                                      variant="flat" 
                                      onPress={() => handleQuantityChange(item.id, -1)}
                                      className="rounded-none"
                                    >
                                      <Icon icon="lucide:minus" width={16} />
                                    </Button>
                                    <span className="px-4">{item.quantity}</span>
                                    <Button 
                                      isIconOnly 
                                      size="sm" 
                                      variant="flat" 
                                      onPress={() => handleQuantityChange(item.id, 1)}
                                      className="rounded-none"
                                    >
                                      <Icon icon="lucide:plus" width={16} />
                                    </Button>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm">{item.price} ₽ за шт.</p>
                                    <p className="font-medium">{item.price * item.quantity} ₽</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <Divider className="my-6" />

                      <div className="space-y-3 mb-6">
                        <div className="flex justify-between">
                          <p className="text-foreground/70">Товары ({totalItems})</p>
                          <p>{totalPrice} ₽</p>
                        </div>
                          <div className="flex justify-between">
                            <p className="text-foreground/70">Доставка</p>
                            <p>{(() => {
                              const isCdek = deliveryMethod === 'cdek';
                              const costKnown = isCdek ? Boolean(selectedTariffCode) : Boolean(deliveryMethod);
                              return costKnown ? `${calculateDelivery()} ₽` : '—';
                            })()}</p>
                          </div>
                        {promoApplied && promoData && promoData.valid && (
                          <div className="flex justify-between text-danger">
                            <p className="text-foreground/70">Скидка{promoData.discount_type === 'percent' && promoData.amount ? ` (${promoData.amount}%)` : ''}</p>
                            <p>-{calculatePromoDiscount()} ₽</p>
                          </div>
                        )}
                      </div>

                      <Divider className="my-6" />

                      <div className="flex justify-between">
                        <p className="font-medium">Итого</p>
                        <p className="font-bold text-lg">{calculateFinalTotal()} ₽</p>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};